import { Search } from '@mui/icons-material';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import { Stop } from '../../../../domain/stop/Stop';
import { useAutoCompleteFocus } from '../../../../hooks/useAutocompleteFocus';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';

interface SearchStopData {
  id: string;
  label?: string;
  location?: string;
}

interface MapSearcherProps {
  stops?: Stop[];
  onSearchStopSelect: (stop: Stop) => void;
}

export const MapStopsSearcher = ({ stops, onSearchStopSelect }: MapSearcherProps) => {
  const intl = useIntl();
  const parentRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<boolean>(false);
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  useAutoCompleteFocus({
    parentRef,
    onFocused: () => {
      setInputFocused(true);
    },
    onBlurred: () => {
      setInputFocused(false);
      setActive(false);
    },
  });

  const handleSearchServiceSelect = (stopData: SearchStopData) => {
    const stop = stops?.find((s) => s.id === stopData.id);
    if (stop) {
      onSearchStopSelect(stop);
    }
    setActive(false);
  };

  if (!stops) {
    return null;
  }

  return (
    <>
      {!active ? (
        <div
          style={{
            position: `absolute`,
            right: `20px`,
            top: `12px`,
            padding: `4px 8px`,
            zIndex: `449`,
          }}
        >
          <Button
            id="search_stops"
            style={{
              borderRadius: `50%`,
              padding: 0,
              width: `32px`,
              height: `32px`,
              margin: 0,
              backgroundColor: `white`,
            }}
            ripple
            onClick={() => {
              setInputFocused(true);
              setActive(true);
            }}
          >
            <Search style={{ width: `20px`, color: `#AAAAAA` }} />
          </Button>
        </div>
      ) : (
        <div
          ref={parentRef}
          style={{
            height: `38px`,
            width: inputFocused ? `450px` : `250px`,
            position: `absolute`,
            right: `20px`,
            top: `12px`,
            padding: `4px 8px`,
            zIndex: `449`,
          }}
        >
          <ReactSearchAutocomplete
            items={stops.map((stop) => ({ id: stop.id, label: stop.label, location: stop.location?.label }))}
            fuseOptions={{
              isCaseSensitive: false,
              minMatchCharLength: 2,
              keys: [`label`, `location`],
            }}
            autoFocus
            placeholder={intl.formatMessage({ id: `planoverview.searcher.placeholder` })}
            styling={{
              fontFamily: `Geist Variable`,
              fontSize: `13px`,
              boxShadow: `none`,
              height: `38px`,
              border: `none`,
              color: RoutalPalette.secondary40,
            }}
            maxResults={15}
            showIcon={false}
            formatResult={(item: SearchStopData) => (
              <>
                <Text
                  tagName="span"
                  variant="body-medium"
                  style={{ display: `block`, textAlign: `left`, cursor: `pointer` }}
                >
                  {item.label}
                </Text>
                <Text tagName="span" variant="label" style={{ display: `block`, textAlign: `left`, cursor: `pointer` }}>
                  {item.location}
                </Text>
              </>
            )}
            showNoResultsText={intl.formatMessage({ id: `no_results` })}
            resultStringKeyName="label"
            onSelect={handleSearchServiceSelect}
          />
        </div>
      )}
    </>
  );
};
